"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import ZoomImage from "./ZoomImage";
import IconButton from "./IconButton";
import { faEllipsisStrokeVertical } from "@fortawesome/pro-light-svg-icons";
import {
  faCalendarCirclePlus,
  faImage,
  faLocationDot,
  faTableLayout,
  faVideo,
  faXmark,
} from "@fortawesome/sharp-light-svg-icons";
import { CaseData } from "@/cms/models";
import { AnimateNumber } from "motion-number";
import {
  createIcsEvent,
  getCaseDetailSublabel,
  getOpenHouseText,
} from "@/utils/helper";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import Link from "next/link";
import RippleCtaButton from "./RippleCtaButton";

interface ImageGalleryProps {
  caseData: CaseData;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ caseData }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filter, setFilter] = useState<"photo" | "drawing" | "video">("photo");
  const [filteredImages, setFilteredImages] = useState(
    caseData.images.filter((x) => x.namespace.endsWith("photo"))
  );
  const nextOpenhouse = caseData.openhouses[0];
  const openHouseText = nextOpenhouse
    ? nextOpenhouse.bookingRequired
      ? `Åbent hus med tilmelding - ${getOpenHouseText(nextOpenhouse)}`
      : `Åbent hus - ${getOpenHouseText(nextOpenhouse)}`
    : undefined;

  useEffect(() => {
    if (!window) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleFullscreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  const handleFullscreen = async (
    open: boolean,
    filter: "photo" | "drawing" | "video" = "photo"
  ) => {
    if (!window) {
      return;
    }

    setFilter(filter);
    setIsFullscreen(open);
    if (open) {
      document.body.style.overflow = "hidden";
      window.history.pushState(
        null,
        "",
        `${window.location.pathname}?fullscreen=true`
      );
    } else {
      document.body.style.overflow = "auto";
      window.history.back();
    }
  };

  useEffect(() => {
    if (!window) {
      return;
    }

    const handlePopState = () => {
      setIsFullscreen(false);
      document.body.style.overflow = "auto";
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    setFilteredImages(
      caseData.images.filter((x) => x.namespace.endsWith(filter))
    );
  }, [filter, caseData.images]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-1 relative">
        {isFullscreen && (
          <div className="fixed inset-0 z-30 flex flex-1 bg-white overflow-auto">
            <div className="flex flex-col flex-1 ">
              <button
                onClick={() => handleFullscreen(false)}
                className="fixed m-4 top-0 right-4 border size-10 flex justify-center items-center z-50 bg-white rounded-full"
              >
                <FontAwesomeIcon className="size-6" icon={faXmark} />
              </button>
              <div className={`flex-col gap-1 flex flex-1`}>
                {filteredImages.map((image, index) => (
                  <div
                    key={`detailimage-${index}`}
                    className="flex flex-1  justify-center relative w-full"
                  >
                    <div
                      data-loaded="false"
                      className="relative data-[loaded=false]:opacity-0 data-[loaded=true]:opacity-100 transition-opacity duration-300"
                      id={`image-container-${index}`}
                    >
                      {image.description && (
                        <div
                          id={`image-description-${index}`}
                          className="absolute  bg-white p-2 left-0 top-0 homepage-text-sm z-10"
                        >
                          {image.description}
                        </div>
                      )}
                      <picture>
                        <source
                          media="(min-width: 640px)"
                          srcSet={encodeURI(image.xlUrl)}
                        />
                        <img
                          id={image.lgUrl}
                          src={encodeURI(image.lgUrl)}
                          alt={`Image ${index}`}
                          className="object-contain max-w-full max-h-[95vh] data-[loaded=false]:h-[95vh]"
                          loading={index < 2 ? "eager" : "lazy"}
                          data-loaded="false"
                          onLoad={(e) => {
                            e.currentTarget.dataset.loaded = "true";
                            const parentDiv = document.getElementById(
                              `image-container-${index}`
                            );
                            if (parentDiv) {
                              parentDiv?.setAttribute("data-loaded", "true");
                            }
                          }}
                        />
                      </picture>
                    </div>
                  </div>
                ))}
                {caseData.videoUrls?.length > 0 &&
                  filter === "video" &&
                  caseData.videoUrls.map((videoUrl, index) => (
                    <video
                      key={`${videoUrl}-${index}`}
                      controls
                      className="max-h-[90vh] w-full"
                    >
                      <source src={videoUrl} />
                    </video>
                  ))}{" "}
              </div>
            </div>
          </div>
        )}
        {caseData.images[0] ? (
          <div className="w-full flex flex-col md:flex-row flex-1 gap-1 pt-1">
            <div className="hidden md:flex overflow-hidden flex-1 relative h-[500px]">
              <ZoomImage
                onClick={() => handleFullscreen(true)}
                src={caseData.images[0]?.lgUrl}
                alt="image 01"
              />
              {(caseData.marketingTexts?.label ||
                getCaseDetailSublabel(caseData) ||
                (caseData.isActive && caseData.openhouses.length > 0)) && (
                <div className="hidden homepage-text-sm md:flex gap-2 flex-col absolute top-0 inset-x-0 bg-opacity-80 bg-white p-2">
                  {caseData.marketingTexts?.label && (
                    <span className="homepage-text-lg uppercase">
                      {caseData.marketingTexts?.label}
                    </span>
                  )}
                  {getCaseDetailSublabel(caseData) && (
                    <span className="homepage-text-lg uppercase">
                      {getCaseDetailSublabel(caseData)}
                    </span>
                  )}
                  {caseData.isActive && caseData.openhouses.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span>{openHouseText}</span>
                        <button
                          aria-label="download calendar appointment"
                          onClick={() => {
                            const openhouse = caseData.openhouses[0];
                            const icsContent = createIcsEvent(
                              openHouseText!,
                              new Date(openhouse.startDate!),
                              new Date(openhouse.endDate!),
                              openhouse.location
                            );
                            const blob = new Blob([icsContent], {
                              type: "text/calendar",
                            });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                            link.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          <FontAwesomeIcon
                            className="size-5"
                            icon={faCalendarCirclePlus}
                          />
                        </button>
                      </div>
                      <div>
                        {caseData.openhouses.find((x) => x.bookingRequired) && (
                          <RippleCtaButton
                            onClick={(e) => {
                              if (!window) {
                                return;
                              }

                              window.dispatchEvent(
                                new CustomEvent("selectedTabEvent", {
                                  detail: { selectedTab: "openhouse" },
                                })
                              );
                              e.preventDefault();
                              document
                                .getElementById("caseContactSection")
                                ?.scrollIntoView({ behavior: "smooth" });
                            }}
                          >
                            Tilmeld
                          </RippleCtaButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex md:hidden overflow-hidden relative h-[300px]">
              <ZoomImage
                onClick={() => handleFullscreen(true)}
                src={caseData.images[0]?.lgUrl}
                alt="image 01"
              />
            </div>
            {caseData.images[1] && (
              <div className="flex md:hidden h-[180px] gap-1">
                <div className="flex overflow-hidden flex-1 relative">
                  <ZoomImage
                    onClick={() => handleFullscreen(true)}
                    src={caseData.images[1]?.lgUrl}
                    alt="image 02"
                  />
                </div>

                {caseData.images[2] && (
                  <div className="flex overflow-hidden flex-1 relative">
                    <ZoomImage
                      onClick={() => handleFullscreen(true)}
                      src={caseData.images[2]?.lgUrl}
                      alt="image 03"
                    />
                  </div>
                )}
              </div>
            )}
            {caseData.images[1] && (
              <div className="hidden md:flex flex-1 flex-col gap-1">
                <div className="flex overflow-hidden flex-1 relative">
                  <ZoomImage
                    onClick={() => handleFullscreen(true)}
                    src={caseData.images[1]?.lgUrl}
                    alt="image 02"
                  />
                </div>

                {caseData.images[2] && (
                  <div className="flex flex-1 gap-1">
                    <div className="flex overflow-hidden flex-1 relative">
                      <ZoomImage
                        onClick={() => handleFullscreen(true)}
                        src={caseData.images[2]?.lgUrl}
                        alt="image 03"
                      />
                    </div>

                    {caseData.images[3] && (
                      <div className="flex flex-1 relative overflow-hidden">
                        <ZoomImage
                          onClick={() => handleFullscreen(true)}
                          src={caseData.images[3]?.lgUrl}
                          alt="image 04"
                        >
                          <div className="absolute inset-0 bg-gray-900 bg-opacity-50">
                            <div className="flex flex-1 h-full gap-2 homepage-text-sm text-white items-center justify-center">
                              <FontAwesomeIcon
                                className="size-6"
                                icon={faImage}
                              />
                              <AnimateNumber
                                format={{
                                  style: "decimal",
                                }}
                                transition={{
                                  delay: 0.05,
                                  duration: 0.2,
                                  ease: "easeOut",
                                }}
                                style={{ originX: 0.5, originY: 1.5 }}
                                className="text-xl"
                              >
                                {caseData.images?.length}
                              </AnimateNumber>
                            </div>
                          </div>
                        </ZoomImage>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="hidden md:flex w-full h-[500px] bg-gray-300"></div>
        )}
        {!isFullscreen && caseData.images?.length > 0 && (
          <div className="absolute flex -bottom-7 inset-x-0 z-20 justify-center">
            <div className="h-14 flex shadow-lg homepage-button-rounded overflow-auto">
              <IconButton
                suppressRoundedClass={true}
                icon={faImage}
                onClick={() => handleFullscreen(true)}
                ariaLabel="Billeder"
                label="Billeder"
              />
              {caseData.images.some((x) => x.namespace.endsWith("drawing")) && (
                <IconButton
                  suppressRoundedClass={true}
                  icon={faTableLayout}
                  onClick={() => {
                    handleFullscreen(true, "drawing");
                  }}
                  ariaLabel="Plantegning"
                  label="Plantegning"
                />
              )}
              {caseData.videoUrls?.length > 0 && (
                <IconButton
                  suppressRoundedClass={true}
                  icon={faVideo}
                  onClick={() => {
                    handleFullscreen(true, "video");
                  }}
                  ariaLabel="Video"
                  label="Video"
                />
              )}
              <IconButton
                suppressRoundedClass={true}
                icon={faLocationDot}
                onClick={() => {
                  document
                    .getElementById("caseMapSection")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                ariaLabel="Kort"
                label="Kort"
              />
              {caseData.videoLinks?.length > 0 && (
                <Listbox>
                  <ListboxButton className="bg-[var(--casedetails-submenu-bg-color)] text-[var(--casedetails-submenu-text-color)] hover:text-[var(--casedetails-submenu-hover-text-color)] hover:bg-[var(--casedetails-submenu-hover-bg-color)] px-4 py-2">
                    <FontAwesomeIcon
                      className="size-5"
                      icon={faEllipsisStrokeVertical}
                    />
                  </ListboxButton>
                  <ListboxOptions
                    transition
                    anchor="bottom"
                    className="border shadow-lg bg-[var(--dropdown-bg-color)] py-1"
                  >
                    {caseData.videoLinks.map((link) => (
                      <ListboxOption
                        key={link.url}
                        value={link.url}
                        className="cursor-pointer homepage-text-sm hover:text-[var(--dropdown-hover-text-color)] text-[var(--dropdown-text-color)] bg-[var(--dropdown-bg-color)] hover:bg-[var(--dropdown-hover-bg-color)] px-4 py-2 "
                      >
                        <Link target={link.url} href={link.url}>
                          {link.displayName}
                        </Link>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Listbox>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col md:hidden pt-10 pb-4 justify-center items-center">
        {caseData.marketingTexts?.label && (
          <div className="homepage-text-lg px-4 uppercase">
            {caseData.marketingTexts?.label}
          </div>
        )}
        {getCaseDetailSublabel(caseData) && (
          <span className="homepage-text-lg  px-4 uppercase">
            {getCaseDetailSublabel(caseData)}
          </span>
        )}
        {caseData.isActive && caseData.openhouses.length > 0 && (
          <div className="flex homepage-text-sm flex-col gap-2 px-4">
            <div className="flex items-center gap-2">
              <span>{openHouseText}</span>
            </div>
            <div>
              {caseData.openhouses.find((x) => x.bookingRequired) && (
                <div className="flex gap-2">
                  <RippleCtaButton
                    onClick={() => {
                      const icsContent = createIcsEvent(
                        openHouseText!,
                        new Date(caseData.openhouses[0].startDate!),
                        new Date(caseData.openhouses[0].endDate!)
                      );
                      const blob = new Blob([icsContent], {
                        type: "text/calendar",
                      });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <FontAwesomeIcon
                      className="size-5"
                      icon={faCalendarCirclePlus}
                    />
                  </RippleCtaButton>
                  <RippleCtaButton
                    onClick={(e) => {
                      if (!window) {
                        return;
                      }

                      window.dispatchEvent(
                        new CustomEvent("selectedTabEvent", {
                          detail: { selectedTab: "openhouse" },
                        })
                      );
                      e.preventDefault();
                      document
                        .getElementById("caseContactSection")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Tilmeld
                  </RippleCtaButton>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
